import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useScheduleTable } from '../../../../../Application/SchedulePage/context';

const CompleteSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
  justify-content: flex-end;
  margin-top: 2rem;
  padding: 1rem 0;
  border-top: 1px solid #e5e7eb;
`;

const ValidationMessage = styled.div`
  color: #A6031f;
  font-size: 0.875rem;
  font-family: 'Manrope', sans-serif;
  font-weight: 500;
  text-align: center;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  max-width: 600px;
`;

const StyledCompleteButton = styled.button`
  background-color: ${props => {
        switch (props.state) {
            case 'ready': return 'var(--color-primary)';
            case 'submitted': return '#6b7280';
            case 'disabled':
            default: return '#9ca3af';
        }
    }};
  
  color: white;
  border: none;
  padding: 0.875rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.state === 'ready' ? 'pointer' : 'not-allowed'};
  transition: all 0.2s ease-in-out;
  min-width: 200px;
  
  &:hover {
    background-color: ${props => {
        if (props.state === 'ready') return 'var(--color-secondary)';
        return props.state === 'submitted' ? '#6b7280' : '#9ca3af';
    }};
    
    ${props => props.state === 'ready' && `
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(5, 150, 105, 0.2);
    `}
  }
  
  &:active {
    transform: ${props => props.state === 'ready' ? 'translateY(0)' : 'none'};
  }
  
  &:focus {
    outline: none;
    box-shadow: ${props => {
        if (props.state === 'ready') return '0 0 0 3px rgba(5, 150, 105, 0.3)';
        return 'none';
    }};
  }
`;


function CompleteButton({
    data,
    requiredFields,
    onSubmit,
    customValidation,
    customValidationMessage
}) {
    const [hasChangesAfterSubmit, setHasChangesAfterSubmit] = useState(false);
    const { isSubmitted, setIsSubmitted, lastDataHash, setLastDataHash } = useScheduleTable();
    const generateDataHash = useCallback((data) => {
        return JSON.stringify(data);
    }, []);

    useEffect(() => {
        const currentHash = generateDataHash(data);

        if (isSubmitted && currentHash !== lastDataHash) {
            setHasChangesAfterSubmit(true);
        }
    }, [data, generateDataHash, isSubmitted, lastDataHash]);

    const validateAllFields = useCallback(() => {
        if (customValidation) {
            return customValidation();
        }
        if (data.length === 0) {
            return {
                isValid: false,
                message: 'Please add at least one property to complete the schedule.',
            };
        }

        return {
            isValid: true,
            message: null,
        };
    }, [data, customValidation]);

    const validation = useMemo(() => {
        return validateAllFields();
    }, [validateAllFields]);

    const buttonState = useMemo(() => {
        if (!validation.isValid) return 'disabled';
        if (isSubmitted && !hasChangesAfterSubmit) return 'disabled';
        return 'ready';
    }, [validation.isValid, isSubmitted, hasChangesAfterSubmit]);

    const validationMessage = useMemo(() => {
        if (customValidationMessage) {
            return customValidationMessage();
        }
        return validation.message;
    }, [customValidationMessage, validation.message]);

    const handleComplete = useCallback(() => {
        if (buttonState === 'ready') {
            onSubmit();

            setIsSubmitted(true);
            setHasChangesAfterSubmit(false);
            setLastDataHash(generateDataHash(data));
        }
    }, [buttonState, data, onSubmit, generateDataHash]);


    return (
        <CompleteSectionContainer>
            {buttonState === 'disabled' && validationMessage && (
                <ValidationMessage>
                    {validationMessage}
                </ValidationMessage>
            )}

            <StyledCompleteButton
                state={buttonState}
                onClick={handleComplete}
                disabled={buttonState !== 'ready'}
            >
                Complete
            </StyledCompleteButton>
        </CompleteSectionContainer>
    );
}

CompleteButton.propTypes = {
    data: PropTypes.array.isRequired,
    group: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(['all'])]).isRequired,
    requiredFields: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
    onSubmit: PropTypes.func.isRequired,
    validationErrors: PropTypes.object,
    onValidationChange: PropTypes.func,
    customValidation: PropTypes.func,
    customValidationMessage: PropTypes.func,
};

CompleteButton.defaultProps = {
    validationErrors: {},
    onValidationChange: () => { },
    customValidation: null,
    customValidationMessage: null,
};

export default CompleteButton;
